/** B2 — Libro de bienes de inversión y su amortización lineal por meses. */
import { useEffect, useState } from 'react'
import { AlertCircle, Download, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { downloadFile } from '../../lib/download'
import { formatCents } from '../../lib/money'
import type { InvestmentResponse } from '@toga/shared'
import { Note, selectCls, td, tdR, th, thR } from './common'

export default function InvestmentsTab({ issuerId, year }: { issuerId: string; year: number }) {
  const [data, setData] = useState<InvestmentResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    api
      .get<InvestmentResponse>(`/books/investments?year=${year}&issuerId=${issuerId}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [issuerId, year])

  const exportCsv = async () => {
    setBusy(true)
    try {
      await downloadFile(
        `/books/investments.csv?year=${year}&issuerId=${issuerId}`,
        `libro-bienes-inversion-${year}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo exportar')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="px-5 py-3 border-b border-base flex items-center justify-between gap-2">
        <p className="text-xs t-muted">Situación al cierre de {year}</p>
        <button
          type="button"
          disabled={busy}
          onClick={() => void exportCsv()}
          className={`${selectCls} inline-flex items-center gap-1.5 hover:bg-hover disabled:opacity-50`}
        >
          <Download size={13} /> CSV para la gestoría
        </button>
      </div>
      {error && (
        <div className="m-4 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}
      {!data && !error && (
        <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      )}
      {data && data.rows.length === 0 && (
        <div className="px-5 py-10 text-center t-muted text-sm">
          No hay bienes de inversión. Cuando anotes una factura de equipos o mobiliario como «bien
          de inversión», aparecerá aquí con su amortización.
        </div>
      )}
      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-base">
                <th className={th}>Adquisición</th>
                <th className={th}>Bien</th>
                <th className={thR}>Valor</th>
                <th className={thR}>Vida útil</th>
                <th className={thR}>Amort. anterior</th>
                <th className={thR}>Amort. {year}</th>
                <th className={thR}>Amort. acumulada</th>
                <th className={thR}>Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-base/50">
                  <td className={`${td} whitespace-nowrap`}>
                    {new Date(r.acquisitionDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
                  </td>
                  <td className={td}>
                    <div className="t-primary">{r.description ?? r.supplierName}</div>
                    <div className="text-[11px] t-faint">
                      {r.supplierName}
                      {r.invoiceNumber ? ` · ${r.invoiceNumber}` : ''}
                    </div>
                  </td>
                  <td className={tdR}>{formatCents(r.baseCents)}</td>
                  <td className={tdR}>{r.usefulLifeYears} años</td>
                  <td className={tdR}>{formatCents(r.accumulatedBeforeCents)}</td>
                  <td className={`${tdR} font-semibold`}>
                    {formatCents(r.amortizationYearCents)}
                    {r.deductiblePct < 100 && (
                      <span className="block text-[10px] t-faint font-normal">
                        deducible {formatCents(r.deductibleAmortizationYearCents)}
                      </span>
                    )}
                  </td>
                  <td className={tdR}>{formatCents(r.accumulatedCents)}</td>
                  <td className={tdR}>{formatCents(r.netValueCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-base font-semibold">
                <td className={td} colSpan={2}>
                  Total
                </td>
                <td className={tdR}>{formatCents(data.totals.baseCents)}</td>
                <td className={td} />
                <td className={td} />
                <td className={tdR}>{formatCents(data.totals.amortizationYearCents)}</td>
                <td className={tdR}>{formatCents(data.totals.accumulatedCents)}</td>
                <td className={tdR}>{formatCents(data.totals.netValueCents)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <Note>
        Amortización lineal por meses: el mes de adquisición cuenta entero y el bien nunca se
        amortiza por encima de su valor. La vida útil la fijas tú al anotar la factura (tu gestoría
        te dice la que corresponde). El IVA de un bien de inversión se deduce en el trimestre de la
        factura, no se reparte.
      </Note>
    </div>
  )
}
